import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconSparkles2, IconPlayerStopFilled } from '@tabler/icons-react';
import { ArrowUp, Maximize2, Minimize2, X } from 'lucide-react';
import RelatoriosOryaMarkdownView from '../relatorios/components/RelatoriosOryaMarkdownView';
import OryaThinkingStatus from '../documentation/components/OryaThinkingStatus';
import { sendDocumentationOryaQuery } from '../documentation/documentationOryaService';
import {
  buildConfigOryaContextPath,
  buildConfigOryaQuestionWithContext,
  getConfigOryaFirstName,
} from './configOryaContext';
import './ConfigOryaAssistant.css';

function createMessageId() {
  return `cfg-orya-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function buildOryaHistory(messages) {
  const items = (messages || []).filter((m) => !m.isError);
  const history = [];
  for (let i = 0; i + 1 < items.length; i += 2) {
    const user = items[i];
    const assistant = items[i + 1];
    if (user?.role !== 'user' || assistant?.role !== 'assistant') continue;
    const userContent = String(user.apiContent || user.content || '').trim();
    const assistantContent = String(assistant.content || '').trim();
    if (!userContent || !assistantContent) continue;
    history.push({ role: 'user', content: userContent });
    history.push({ role: 'assistant', content: assistantContent });
  }
  return history;
}

export default function ConfigOryaMiniChat({
  onClose,
  isClosing = false,
  userDisplayName = '',
  configSection = 'process',
  submenuId = null,
}) {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const firstName = getConfigOryaFirstName(userDisplayName);
  const hasConversation = messages.length > 0;

  const contextPath = useMemo(
    () => buildConfigOryaContextPath({ section: configSection, submenuId }),
    [configSection, submenuId]
  );

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (!isClosing) return undefined;
    setIsVisible(false);
    return undefined;
  }, [isClosing]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  useEffect(() => {
    if (isVisible && !isClosing) {
      const t = setTimeout(() => inputRef.current?.focus(), 320);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isVisible, isClosing]);

  const resizeInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, 120);
    el.style.height = `${Math.max(next, 20)}px`;
  }, []);

  useEffect(() => {
    resizeInput();
  }, [question, resizeInput, isVisible]);

  const submitQuestion = useCallback(async () => {
    const text = question.trim();
    if (!text || sending) return;

    const apiQuestion = buildConfigOryaQuestionWithContext(text, contextPath);
    const userMsg = {
      id: createMessageId(),
      role: 'user',
      content: text,
      apiContent: apiQuestion,
    };
    const history = buildOryaHistory(messages);

    setMessages((prev) => [...prev, userMsg]);
    setQuestion('');
    setSending(true);

    try {
      const data = await sendDocumentationOryaQuery({
        question: apiQuestion,
        history,
      });
      const answer = String(data?.answer || '').trim();
      setMessages((prev) => [
        ...prev,
        {
          id: createMessageId(),
          role: 'assistant',
          content: answer || 'Não encontrei uma resposta na documentação.',
          sources: Array.isArray(data?.sources_used) ? data.sources_used : [],
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: createMessageId(),
          role: 'assistant',
          content: err.message || 'Erro ao consultar a Orya.',
          isError: true,
        },
      ]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [question, sending, messages, contextPath]);

  const handleSubmit = (e) => {
    e.preventDefault();
    submitQuestion();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitQuestion();
    }
  };

  return (
    <div
      className={[
        'config-orya-mini-chat',
        isVisible && !isClosing ? 'config-orya-mini-chat--visible' : '',
        isExpanded ? 'config-orya-mini-chat--expanded' : '',
        isClosing ? 'config-orya-mini-chat--closing' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="dialog"
      aria-label="Chat com a Orya"
      aria-modal="false"
    >
      <header className="config-orya-mini-chat-header">
        <div className="config-orya-mini-chat-title">
          <IconSparkles2 size={16} stroke={1.75} aria-hidden />
          <span>Orya</span>
        </div>
        <div className="config-orya-mini-chat-header-actions">
          <button
            type="button"
            className="config-orya-mini-chat-icon-btn"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-label={isExpanded ? 'Reduzir chat' : 'Expandir chat'}
            aria-pressed={isExpanded}
          >
            {isExpanded ? (
              <Minimize2 size={15} strokeWidth={2} aria-hidden />
            ) : (
              <Maximize2 size={15} strokeWidth={2} aria-hidden />
            )}
          </button>
          <button
            type="button"
            className="config-orya-mini-chat-icon-btn"
            onClick={onClose}
            aria-label="Fechar assistente"
          >
            <X size={16} strokeWidth={2} aria-hidden />
          </button>
        </div>
      </header>

      <div className="config-orya-mini-chat-body">
        {!hasConversation && !sending ? (
          <div className="config-orya-mini-chat-welcome">
            <h2>Olá, {firstName}!</h2>
            <p>Como posso te ajudar?</p>
          </div>
        ) : (
          <div className="config-orya-mini-chat-feed" role="log" aria-label="Conversa com a Orya">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`config-orya-mini-chat-msg config-orya-mini-chat-msg--${msg.role}`}
              >
                {msg.role === 'assistant' ? (
                  msg.isError ? (
                    <p className="config-orya-mini-chat-error" role="alert">
                      {msg.content}
                    </p>
                  ) : (
                    <RelatoriosOryaMarkdownView markdown={msg.content} canExport={false} />
                  )
                ) : (
                  <p className="config-orya-mini-chat-user-text">{msg.content}</p>
                )}
              </div>
            ))}
            {sending ? <OryaThinkingStatus active={sending} /> : null}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <form className="config-orya-mini-chat-footer" onSubmit={handleSubmit}>
        <div className="config-orya-mini-chat-input-wrap">
          <textarea
            ref={inputRef}
            className="config-orya-mini-chat-input"
            placeholder="Pergunte-me"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onInput={resizeInput}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={sending}
            aria-label="Pergunta para a Orya"
          />
          <button
            type="submit"
            className="config-orya-mini-chat-send"
            aria-label={sending ? 'Processando pergunta' : 'Enviar pergunta'}
            disabled={!question.trim() || sending}
          >
            {sending ? (
              <IconPlayerStopFilled size={13} stroke={0} aria-hidden />
            ) : (
              <ArrowUp size={15} strokeWidth={2.5} aria-hidden />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
